// Utility helpers for ActivityItem

/**
 * Removes a leading user card anchor from activity HTML content.
 * Matches and strips patterns like:
 *   <p><a class="user-link user-card" ...>Name</a>:</p>
 *   <a class="user-link user-card" ...>Name</a>:
 * Repeats removal if it occurs multiple times.
 */
export function stripUserCard(html: string): string {
  if (!html) return html;

  let output = html;

  // Matches an optional opening <p>, then an <a class="... user-card ...">...</a>:
  // and an optional closing </p>, allowing whitespace around pieces.
  const leadingUserCard =
    /^(\s*(?:<p[^>]*>\s*)?)<a\b[^>]*class=(?:"|')[^"']*\buser-card\b[^"']*(?:"|')[^>]*>.*?<\/a>:\s*(?:<\/p>\s*)?/i;

  // Strip repeated leading user-card anchors (some payloads double-render this)
  let guard = 0;
  while (leadingUserCard.test(output) && guard < 10) {
    output = output.replace(leadingUserCard, '');
    guard += 1;
  }

  // Clean up any now-empty leading paragraphs or line-breaks
  output = output.replace(/^\s*(?:<p[^>]*>\s*<\/p>\s*)+/i, '');
  output = output.replace(/^\s*(?:<br\s*\/?>(?:\s|\n|\r)*)+/i, '');

  return output;
}

const isEmbedCandidate = (raw: string): boolean => {
  if (!raw) return false;
  if (!/^https?:\/\//i.test(raw)) return false;
  if (raw.includes('cdn.exercise.com')) return false;

  // Filter out obvious image URLs
  const lower = raw.toLowerCase();
  const imageExts = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp', '.avif'];
  if (imageExts.some((ext) => lower.includes(ext))) {
    return false;
  }

  try {
    const url = new URL(raw);
    const host = url.hostname.toLowerCase();

    const youtubeHosts = ['www.youtube.com', 'youtube.com', 'youtu.be', 'www.youtu.be'];
    const vimeoHosts = ['vimeo.com', 'www.vimeo.com', 'player.vimeo.com'];

    if (youtubeHosts.includes(host)) {
      return true;
    }

    if (vimeoHosts.includes(host)) {
      return true;
    }
  } catch (_e) {
    return false;
  }

  return false;
};

// Extracts unique http(s) URLs from HTML content for rich embeds.
// Only returns YouTube and Vimeo *page* URLs, never image URLs.
// Still excludes cdn.exercise.com URLs from iframely processing.
export function extractUrlsFromHtml(html: string): string[] {
  if (!html) return [];

  const urls = new Set<string>();

  // 1) Collect hrefs from anchor tags
  const anchorRegex = /<a\b[^>]*href=("|')([^"']+)\1[^>]*>/gi;
  let match: RegExpExecArray | null;
  while ((match = anchorRegex.exec(html)) !== null) {
    const href = match[2];
    if (isEmbedCandidate(href)) {
      urls.add(href);
    }
  }

  // 2) Collect plain URLs in text nodes (simple heuristic)
  const plainUrlRegex = /(https?:\/\/[^\s<'"]+)/gi;
  let m: RegExpExecArray | null;
  while ((m = plainUrlRegex.exec(html)) !== null) {
    const link = m[1];
    if (isEmbedCandidate(link)) {
      urls.add(link);
    }
  }

  return Array.from(urls);
}
